/**
 * Authentication Context for the NFT marketplace.
 *
 * Provides React-based authentication state and utilities on top of
 * the existing sessionStorage-based auth system. This is a non-breaking
 * addition - all existing direct sessionStorage access continues to work.
 *
 * Features:
 * - Reactive user state (components re-render on login/logout)
 * - Type-safe authentication check (isAuthenticated)
 * - Permission checking via bitmask (hasPermission)
 * - Login/logout helpers that sync with sessionStorage
 * - Multi-tab support via storage event listener
 *
 * @module lib/auth
 */

import React, { createContext, useEffect, useState, useMemo } from 'react';
import { sessionStorageGet, sessionStorageSet, sessionStorageRemove } from './window-shim';

// User token structure from backend
export interface UserToken {
  auth: boolean;
  account: {
    id: number | string;
    rHash: string;
    bmp: number; // bitmask for permissions
    username?: string;
    displayname?: string;
    emailaddress?: string;
    // ... other user fields
  };
  // ... other top-level fields
}

// Auth context type
export interface AuthContextType {
  user: UserToken | null;
  isAuthenticated: boolean;
  login: (userToken: UserToken) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

// Create context with null default (will be provided by AuthProvider)
export const AuthContext = createContext<AuthContextType | null>(null);

// Permission bit positions (must match backend)
const PERMISSIONS = [
  'unused0', 'unused1', 'unused2', 'unused3',
  'unused4', 'unused5', 'unused6', 'admin'
];

// Auth storage keys (same as current implementation)
const TOKEN_KEY = 'token';
const AUTH_KEY = 'authKey';

/**
 * Check if a permission bit is set in the bitmask.
 *
 * Exact replica of the original get_permissions() logic from utils/index.js
 * to ensure 100% compatibility with existing behavior.
 *
 * @param bmp - Bitmask number from user token
 * @param permission - Permission name to check (e.g., 'admin')
 * @returns True if permission bit is set
 */
function checkPermissionBit(bmp: number, permission: string): boolean {
  const perms = [
    'unused0', 'unused1', 'unused2', 'unused3',
    'unused4', 'unused5', 'unused6', 'admin'
  ];
  const id = perms.indexOf(permission);
  if (id === -1) {
    return false;
  }

  // Convert to unsigned binary string (same as original)
  const bstr = (bmp >>> 0).toString(2);
  // Extract the bit at position id (0-based from left)
  return bstr.substring(id, id + 1) === '1';
}

/**
 * AuthProvider component.
 *
 * Wraps the application and provides authentication state via context.
 * Reading from sessionStorage directly is still supported - this is a
 * convenience layer that enables reactive auth state.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserToken | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Read initial auth state from sessionStorage on mount
  useEffect(() => {
    const tokenStr = sessionStorageGet(TOKEN_KEY);
    if (tokenStr) {
      try {
        const parsed = JSON.parse(tokenStr) as UserToken;
        if (parsed?.auth === true) {
          setUser(parsed);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (e) {
        console.warn('AuthProvider: Failed to parse auth token:', e);
        setUser(null);
        setIsAuthenticated(false);
      }
    }
  }, []);

  // Listen for storage changes (multi-tab support)
  useEffect(() => {
    const handleStorageChange = () => {
      const tokenStr = sessionStorageGet(TOKEN_KEY);
      if (tokenStr) {
        try {
          const parsed = JSON.parse(tokenStr) as UserToken;
          if (parsed?.auth === true) {
            setUser(parsed);
            setIsAuthenticated(true);
          } else {
            setUser(null);
            setIsAuthenticated(false);
          }
        } catch {
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        // Token was removed (logout in another tab)
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Login: store token in sessionStorage and update state
  const login = (userToken: UserToken) => {
    // Store in sessionStorage (same format as current system)
    sessionStorageSet(TOKEN_KEY, JSON.stringify(userToken));
    if (userToken?.account?.rHash) {
      sessionStorageSet(AUTH_KEY, userToken.account.rHash);
    }

    setUser(userToken);
    setIsAuthenticated(!!userToken?.auth);
  };

  // Logout: clear sessionStorage and state
  const logout = () => {
    sessionStorageRemove(TOKEN_KEY);
    sessionStorageRemove(AUTH_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  // Check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    if (!user?.account?.bmp) return false;
    return checkPermissionBit(user.account.bmp, permission);
  };

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated,
    login,
    logout,
    hasPermission,
  }), [user, isAuthenticated]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access authentication context.
 *
 * Must be used within an AuthProvider. Returns:
 * - user: full user token object or null
 * - isAuthenticated: boolean
 * - login: function to set auth state
 * - logout: function to clear auth state
 * - hasPermission: check bitmask permission
 *
 * @returns AuthContextType
 * @throws Error if used outside AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
